import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import { Logo } from '@/components/ui/Logo';
import { useNavigate } from 'react-router-dom';
import { 
  FileText, MapPin, Clock, DollarSign, 
  CheckCircle2, ArrowRight, AlertCircle, Globe, ArrowLeft
} from 'lucide-react';

// Visa data for SEO pages
const visaData: Record<string, {
  name: string;
  visaFree: string[];
  visaRequired: string[];
  eVisa: string[];
  processingTime: string;
  cost: string;
  requirements: string[];
  tips: string[];
}> = {
  thailand: {
    name: 'Thailand',
    visaFree: ['USA', 'UK', 'Canada', 'Australia', 'Germany', 'France', 'Japan', 'South Korea'],
    visaRequired: ['India', 'China', 'Nigeria', 'Pakistan'],
    eVisa: ['India', 'China', 'Saudi Arabia'],
    processingTime: '3-5 business days (eVisa)',
    cost: '$40-80 depending on type',
    requirements: [
      'Passport valid for 6+ months',
      'Return/onward ticket',
      'Proof of accommodation',
      'Proof of sufficient funds ($500+ per person)',
      'Passport photo (for visa on arrival)'
    ],
    tips: [
      'Visa-free stay is 30 days (extendable to 60)',
      'Apply for eVisa before arrival if required',
      'Have printed copies of all documents',
      'Immigration may ask for proof of funds'
    ]
  },
  japan: {
    name: 'Japan',
    visaFree: ['USA', 'UK', 'Canada', 'Australia', 'EU countries', 'Singapore', 'South Korea'],
    visaRequired: ['India', 'China', 'Philippines', 'Indonesia'],
    eVisa: [],
    processingTime: '5-7 business days',
    cost: 'Free for many nationalities',
    requirements: [
      'Passport valid for duration of stay',
      'Return/onward ticket',
      'Proof of accommodation',
      'Proof of sufficient funds',
      'Detailed itinerary (for visa applicants)'
    ],
    tips: [
      'Visa-free stay is 90 days for most',
      'Apply at Japanese embassy/consulate',
      'Have all documents in order before arrival',
      'Immigration is strict but fair'
    ]
  },
  italy: {
    name: 'Italy',
    visaFree: ['USA', 'UK', 'Canada', 'Australia', 'Japan', 'South Korea', 'EU/Schengen countries'],
    visaRequired: ['India', 'China', 'Nigeria', 'Pakistan', 'Philippines'],
    eVisa: [],
    processingTime: '15-30 business days',
    cost: '€80 for Schengen visa',
    requirements: [
      'Passport valid for 3+ months beyond stay',
      'Two blank pages in passport',
      'Proof of accommodation',
      'Travel insurance (€30,000 minimum)',
      'Return ticket',
      'Proof of sufficient funds'
    ],
    tips: [
      'Schengen visa allows travel to 27 countries',
      'Apply at Italian embassy/consulate',
      'Book appointment early - high demand',
      'Visa-free visitors can stay 90 days in 180-day period'
    ]
  },
  greece: {
    name: 'Greece',
    visaFree: ['USA', 'UK', 'Canada', 'Australia', 'Japan', 'South Korea', 'EU/Schengen countries'],
    visaRequired: ['India', 'China', 'Nigeria', 'Pakistan', 'Philippines'],
    eVisa: [],
    processingTime: '15-30 business days',
    cost: '€80 for Schengen visa',
    requirements: [
      'Passport valid for 3+ months beyond stay',
      'Two blank pages in passport',
      'Proof of accommodation',
      'Travel insurance (€30,000 minimum)',
      'Return ticket',
      'Proof of sufficient funds'
    ],
    tips: [
      'Schengen visa allows travel to 27 countries',
      'Apply at Greek embassy/consulate',
      'Summer months have longer processing times',
      'Visa-free visitors can stay 90 days in 180-day period'
    ]
  },
  morocco: {
    name: 'Morocco',
    visaFree: ['USA', 'UK', 'Canada', 'Australia', 'EU countries', 'Japan', 'South Korea'],
    visaRequired: ['India', 'China', 'Nigeria', 'Pakistan', 'Philippines'],
    eVisa: [],
    processingTime: '5-15 business days',
    cost: '$30-60 depending on nationality',
    requirements: [
      'Passport valid for 6+ months',
      'Return/onward ticket',
      'Proof of accommodation',
      'Proof of sufficient funds'
    ],
    tips: [
      'Visa-free stay is 90 days for most',
      'Apply at Moroccan embassy if visa required',
      'Keep passport copy separate from original',
      'Register with local police if staying long-term'
    ]
  },
  indonesia: {
    name: 'Indonesia',
    visaFree: ['ASEAN countries for 30 days'],
    visaRequired: ['Nigeria', 'Pakistan', 'Afghanistan'],
    eVisa: ['USA', 'UK', 'Canada', 'Australia', 'EU countries', 'India', 'China', 'Most countries'],
    processingTime: 'Visa on Arrival: Instant | eVisa: 3-5 days',
    cost: 'VOA: $35 for 30 days | eVisa: $35-50',
    requirements: [
      'Passport valid for 6+ months',
      'Return/onward ticket',
      'Proof of accommodation',
      'Passport-size photo',
      'Payment in USD or card'
    ],
    tips: [
      'VOA can be extended once for 30 more days',
      'eVisa available at evisa.imigrasi.go.id',
      'Keep immigration receipt safe',
      'Overstaying incurs $100/day fine'
    ]
  },
  portugal: {
    name: 'Portugal',
    visaFree: ['USA', 'UK', 'Canada', 'Australia', 'Japan', 'South Korea', 'EU/Schengen countries'],
    visaRequired: ['India', 'China', 'Nigeria', 'Pakistan', 'Philippines'],
    eVisa: [],
    processingTime: '15-30 business days',
    cost: '€80 for Schengen visa',
    requirements: [
      'Passport valid for 3+ months beyond stay',
      'Two blank pages in passport',
      'Proof of accommodation',
      'Travel insurance (€30,000 minimum)',
      'Return ticket',
      'Proof of sufficient funds'
    ],
    tips: [
      'Schengen visa allows travel to 27 countries',
      'Apply at Portuguese embassy/consulate',
      'Digital nomad visa available for remote workers',
      'Visa-free visitors can stay 90 days in 180-day period'
    ]
  },
  usa: {
    name: 'United States',
    visaFree: [],
    visaRequired: ['Most countries require B1/B2 visa'],
    eVisa: ['VWP countries (UK, Germany, Japan, Australia, etc.) use ESTA'],
    processingTime: 'ESTA: 72 hours | Visa: Varies widely',
    cost: 'ESTA: $40 | Visa: $185',
    requirements: [
      'Valid passport',
      'ESTA approval or valid visa',
      'Return ticket',
      'Proof of ties to home country',
      'Proof of funds'
    ],
    tips: [
      'Apply for ESTA before booking flights',
      'Visa interviews can have long wait times',
      'Be honest in your visa interview',
      'Bring all supporting documents'
    ]
  }
};

export default function VisaLanding() {
  const { country } = useParams<{ country: string }>();
  const navigate = useNavigate();
  
  const data = country ? visaData[country.toLowerCase()] : null;
  
  if (!data) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-6 text-center">
            <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h1 className="text-xl font-bold mb-2">Visa Info Not Found</h1>
            <p className="text-muted-foreground mb-4">
              We're still working on visa information for this country.
            </p>
            <Button onClick={() => navigate('/')}>
              Explore OdyVoy
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const pageTitle = `${data.name} Visa Requirements | OdyVoy`;
  const pageDescription = `Complete ${data.name} visa guide: requirements, processing time, costs, and tips. Check if you need a visa for ${data.name} with OdyVoy's travel assistant.`;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={`https://odyvoy.com/visa/${country}`} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={`https://odyvoy.com/visa/${country}`} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": `Do I need a visa for ${data.name}?`,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": `Visa requirements for ${data.name} depend on your nationality. Some countries have visa-free access, while others need an eVisa or traditional visa.`
                }
              },
              {
                "@type": "Question",
                "name": `How long does a ${data.name} visa take?`,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": `Processing time: ${data.processingTime}`
                }
              }
            ]
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-background">
        {/* Header */}
        <header className="border-b border-border sticky top-0 bg-background/95 backdrop-blur z-50">
          <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 flex justify-between items-center gap-2">
            <button onClick={() => navigate('/')} className="flex items-center gap-2 shrink-0" aria-label="Go to homepage">
              <Logo className="h-10 w-10 sm:h-14 sm:w-14 md:h-20 md:w-20" alt="OdyVoy logo" />
            </button>
            <div className="flex items-center gap-1 sm:gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="text-foreground h-9 px-2 sm:px-3" aria-label="Back to home">
                <ArrowLeft className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Back to Home</span>
              </Button>
              <ThemeToggle />
              <Button size="sm" onClick={() => navigate('/auth')} className="h-9">
                Plan Trip
              </Button>
            </div>
          </div>
        </header>

        <main>
          {/* Hero */}
          <section className="bg-gradient-to-b from-primary/10 to-background py-16 px-4">
            <div className="max-w-4xl mx-auto text-center">
              <Badge className="mb-4" variant="secondary">
                <Globe className="h-3 w-3 mr-1" />
                Visa Guide
              </Badge>
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
                {data.name} Visa Requirements
              </h1>
              <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
                Everything you need to know about visiting {data.name}. Check requirements, 
                processing times, and get personalized visa guidance.
              </p>
              <Button size="lg" onClick={() => navigate('/auth')} className="w-full sm:w-auto max-w-full whitespace-normal h-auto py-3">
                <FileText className="h-5 w-5 mr-2 shrink-0" />
                <span>Check Requirements for My Passport</span>
              </Button>
            </div>
          </section>

          {/* Quick Info */}
          <section className="py-12 px-4 bg-muted/30">
            <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-4">
              <Card>
                <CardContent className="p-4 flex items-center gap-4">
                  <Clock className="h-8 w-8 text-primary" />
                  <div>
                    <p className="text-sm text-muted-foreground">Processing Time</p>
                    <p className="font-medium">{data.processingTime}</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 flex items-center gap-4">
                  <DollarSign className="h-8 w-8 text-primary" />
                  <div>
                    <p className="text-sm text-muted-foreground">Visa Cost</p>
                    <p className="font-medium">{data.cost}</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* Visa Categories */}
          <section className="py-12 px-4">
            <div className="max-w-4xl mx-auto space-y-8">
              {data.visaFree.length > 0 && (
                <div>
                  <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                    Visa-Free Countries
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {data.visaFree.map((c) => (
                      <Badge key={c} variant="secondary" className="bg-green-500/10 text-green-700 dark:text-green-400">
                        {c}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {data.eVisa.length > 0 && (
                <div>
                  <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                    <FileText className="h-5 w-5 text-cyan-500" />
                    eVisa Available
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {data.eVisa.map((c) => (
                      <Badge key={c} variant="secondary" className="bg-cyan-500/10 text-cyan-700 dark:text-cyan-400">
                        {c}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {data.visaRequired.length > 0 && (
                <div>
                  <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-amber-500" />
                    Visa Required
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {data.visaRequired.map((c) => (
                      <Badge key={c} variant="secondary" className="bg-amber-500/10 text-amber-700 dark:text-amber-400">
                        {c}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* Requirements */}
          <section className="py-12 px-4 bg-muted/30">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl font-bold mb-6">General Requirements</h2>
              <div className="space-y-3">
                {data.requirements.map((req, i) => (
                  <div key={i} className="flex items-start gap-3 p-4 bg-background rounded-lg border">
                    <CheckCircle2 className="h-5 w-5 text-primary mt-0.5" />
                    <span>{req}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Tips */}
          <section className="py-12 px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl font-bold mb-6">Pro Tips</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {data.tips.map((tip, i) => (
                  <Card key={i}>
                    <CardContent className="p-4">
                      <p className="text-sm">{tip}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="py-16 px-4 bg-gradient-to-b from-background to-primary/10">
            <div className="max-w-2xl mx-auto text-center">
              <h2 className="text-3xl font-bold mb-4">
                Get Personalized Visa Guidance
              </h2>
              <p className="text-muted-foreground mb-8">
                OdyVoy checks visa requirements based on your passport and provides 
                step-by-step application guidance for your specific situation.
              </p>
              <Button size="lg" onClick={() => navigate('/auth')}>
                Check My Requirements
                <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
            </div>
          </section>
        </main>

        {/* Footer */}
        <footer className="border-t py-8 px-4">
          <div className="max-w-4xl mx-auto text-center text-sm text-muted-foreground">
            <p>© {new Date().getFullYear()} OdyVoy. All rights reserved.</p>
          </div>
        </footer>
      </div>
    </>
  );
}